import { useReducer, useEffect, useCallback } from 'react'
import api, { IEmployeeAdminSearchBookings, IAdminSearchResult, IEmployeeWithBookings } from '@api'
import { debounce } from 'lodash'
import { Toaster } from '@ui'

enum Actions {
  SEARCH,
  LOAD,
}
interface IState {
  loading: boolean
  query: string
  items: IAdminSearchResult | null
  reqId: number
}

interface SearchAction {
  type: Actions.SEARCH
  query: string
  reqId: number
}
interface LoadAction {
  type: Actions.LOAD
  items: IAdminSearchResult | null
  reqId: number
}
type Action = LoadAction | SearchAction

export enum SearchType {
  EMPLOYEES = 'employees',
  DEPARTMENTS = 'departments',
  NEIGHBORHOODS = 'neighborhoods',
  SPACES = 'spaces',
  ALL = 'all',
}

export interface UseAdminSearch extends IState {
  run: (query: string) => void
  reload: () => void
}

function reducer(state: IState, action: Action) {
  switch (action.type) {
    case Actions.SEARCH:
      return {
        ...state,
        reqId: action.reqId,
        query: action.query,
        loading: true,
      }
    case Actions.LOAD:
      return state.reqId > action.reqId
        ? state
        : {
            ...state,
            items: action.items,
            loading: false,
          }
    default:
      return state
  }
}

function convertToMap(empBookings: IEmployeeWithBookings[]) {
  return empBookings.reduce(
    (agg, { id, bookings }) => ({
      ...agg,
      [id]: bookings,
    }),
    {}
  )
}

export default function useAdminSearch({
  apId,
  searchType = SearchType.ALL,
}: {
  apId: string
  searchType?: SearchType
}): UseAdminSearch {
  const [state, dispatch] = useReducer(reducer, {
    reqId: 0,
    loading: false,
    query: '',
    items: null,
  })

  const debouncedSearch = useCallback(
    debounce(async (query: string, reqId: number, searchType: SearchType, apId: string) => {
      const fetchEmployeeBooking = true

      if (query.length >= 3) {
        try {
          const searchResults = await api.admin.search({ query, searchType, apId })

          const ids: string[] = searchResults.employees
            ? searchResults.employees.edges.map(({ node }) => node.id)
            : []

          let empBookings = null

          if (fetchEmployeeBooking && Boolean(ids.length)) {
            empBookings = await api.admin.employeeBookings({ ids })

            const bMap: { [id: string]: IEmployeeAdminSearchBookings[] } = convertToMap(empBookings)

            searchResults.employees.edges.forEach(({ node }) => {
              node.bookings = bMap[node.id]
            })
          }

          dispatch({
            type: Actions.LOAD,
            reqId,
            items: searchResults,
          })
        } catch (error: any) {
          Toaster.fail({ error })
        }
      }
    }, 300),
    []
  )

  useEffect(() => {
    const reqId = Date.now()

    if (!state.query) {
      return dispatch({
        type: Actions.LOAD,
        reqId,
        items: null,
      })
    }

    debouncedSearch(state.query, reqId, searchType, apId)

    return () => debouncedSearch.cancel()
  }, [state.query, debouncedSearch, searchType, apId])

  return {
    ...state,
    reload: () => debouncedSearch(state.query, Date.now(), searchType, apId),
    run: (query: string) =>
      dispatch({
        type: Actions.SEARCH,
        query,
        reqId: Date.now(),
      }),
  }
}
